import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { User, Building2, Shield } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getStylingRolesForLocation } from '@/lib/permissions';
import { useUserRoleAssignments } from '@/hooks/useUserRoleAssignments';

interface UserRole {
  line_id: string;
  role: string;
}

interface UserProfile {
  full_name: string | null;
}

export const UserProfileMenu = () => {
  const { t } = useTranslation();
  const {
    user,
    signOut,
    isSuperAdmin,
    userTenants,
    currentTenantId,
    setCurrentTenantId,
    allLines,
    accessibleLineIds,
    currentLineId,
    setCurrentLineId,
    currentFactoryId,
    setCurrentFactoryId,
    currentAreaId,
    setCurrentAreaId,
  } = useAuth();
  const { data: assignments = [] } = useUserRoleAssignments();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      void fetchUserData();
    }
  }, [user, currentTenantId, allLines, assignments]);

  const fetchUserData = async () => {
    if (!user) return;

    try {
      // Fetch profile
      const { data: profileData } = await ((supabase as any)
        .schema('core'))
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      setProfile(profileData);

      if (currentTenantId) {
        const rolesData: UserRole[] = allLines.flatMap((line) => {
          const derivedRoles = getStylingRolesForLocation(assignments, {
            factoryId: line.factory_id,
            areaId: line.area_id,
            lineId: line.id,
          });

          return derivedRoles.map((role) => ({
            line_id: line.id,
            role,
          }));
        });

        setRoles(rolesData);
      } else {
        setRoles([]);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'operator':
        return 'bg-blue-500 hover:bg-blue-600';
      case 'quality':
        return 'bg-purple-500 hover:bg-purple-600 text-white';
      case 'site_manager':
        return 'bg-primary hover:bg-primary/90';
      default:
        return '';
    }
  };

  if (!user || loading) return null;

  const accessibleLines = allLines.filter(line => accessibleLineIds.has(line.id));
  const factoryAreas = Array.from(new Map(
    accessibleLines.map(line => [
      `${line.factory_id}:${line.area_id}`,
      {
        factory_id: line.factory_id,
        factory_name: line.factory_name,
        area_id: line.area_id,
        area_name: line.area_name,
      },
    ])
  ).values());

  const factoryAreaOptions = Array.from(factoryAreas).map(option => ({
    id: `${option.factory_id}:${option.area_id}`,
    label: `${option.factory_name} - ${option.area_name}`,
    factory_id: option.factory_id,
    area_id: option.area_id,
  }));

  const handleTenantChange = (tenantId: string) => {
    setCurrentTenantId(tenantId);
    setCurrentFactoryId(null);
    setCurrentAreaId(null);
    setCurrentLineId(null);
  };

  const handleFactoryAreaChange = (value: string) => {
    const selected = factoryAreaOptions.find(option => option.id === value);
    if (!selected) return;

    setCurrentFactoryId(selected.factory_id);
    setCurrentAreaId(selected.area_id);

    const nextLine = accessibleLines.find(line =>
      line.factory_id === selected.factory_id && line.area_id === selected.area_id
    );

    setCurrentLineId(nextLine?.id || null);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-10 w-10 rounded-full">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-primary text-primary-foreground">
              {getInitials(profile?.full_name || user.email)}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-3">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <div className="flex flex-col flex-1">
                <p className="text-sm font-medium leading-none">
                  {profile?.full_name || t('userProfileMenu.userFallback')}
                </p>
                <p className="text-xs leading-none text-muted-foreground mt-1">
                  {user.email}
                </p>
              </div>
            </div>

            {isSuperAdmin && (
              <div className="flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-md">
                <Shield className="h-3 w-3 text-primary" />
                <span className="text-xs font-medium text-primary">{t('userManagement.roles.superAdmin.label')}</span>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">{t('userProfileMenu.context')}</span>
              </div>
              <div className="space-y-2 pl-6">
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">{t('userProfileMenu.tenant')}</span>
                  <Select value={currentTenantId || ''} onValueChange={handleTenantChange}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder={t('userProfileMenu.selectTenant')} />
                    </SelectTrigger>
                    <SelectContent>
                      {userTenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-muted-foreground">{t('userProfileMenu.factoryArea')}</span>
                  <Select
                    value={currentFactoryId && currentAreaId ? `${currentFactoryId}:${currentAreaId}` : ''}
                    onValueChange={handleFactoryAreaChange}
                    disabled={!isSuperAdmin && accessibleLines.length === 0}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder={t('userProfileMenu.selectFactoryArea')} />
                    </SelectTrigger>
                    <SelectContent>
                      {factoryAreaOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!isSuperAdmin && accessibleLines.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      {t('userProfileMenu.noLineAccess')}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {roles.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">{t('userProfileMenu.rolesByLine')}</span>
                </div>
                <div className="space-y-2 pl-6">
                  {roles.map((roleInfo, index) => {
                    const lineName = allLines.find(line => line.id === roleInfo.line_id)?.name || t('userProfileMenu.lineFallback');
                    return (
                      <div key={index} className="flex flex-col gap-1">
                        <span className="text-xs font-medium">{lineName}</span>
                        <Badge className={`w-fit text-xs ${getRoleBadgeClass(roleInfo.role)}`}>
                          {t(`roles.${roleInfo.role}`, { defaultValue: roleInfo.role })}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut} className="text-destructive cursor-pointer">
          {t('app.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
